import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import LoadingButton from '@mui/lab/LoadingButton';
import { useReactToPrint } from 'react-to-print';
import BillPrint from './BillPrint';
import EmiSection from './EmiSection';
import PageHeader from '../common/PageHeader';
import MasterDrawer from '../Master/create/MasterDrawer';
import ProductForm from '../Product/ProductForm';
import ProductProperties from '../Product/ProductProperties';
import { useGetBrandsQuery, useGetMasterPageMutation, useGetModelsByBrandQuery } from '../../api/master/MasterApi';
import {
  useGetAvailableImeisByProductQuery,
  useGetOpenBatchesQuery,
  useGetProductsByModelQuery,
  useLazyFindByImeiQuery,
  useLazyFindProductByBarcodeQuery,
  useLazyGetProductByIdQuery,
  useLazySearchProductsQuery,
} from '../../api/product/ProductApi';
import {
  useGetShopSettingQuery,
  useLazyGetBillByIdQuery,
  useLazySearchCustomersQuery,
  useSaveBillMutation,
  useUpdateBillMutation,
} from '../../api/billing/BillApi';
import { Bill, BillEmi, BillRow, BillTender, Customer, PaymentType } from '../../types/bill';
import { Product } from '../../types/product';
import { MasterRow } from '../../types/master';
import { useSnackbar } from '../../utils/snackbar';
import { digitsOnly } from '../../utils/clean';
import { shortDate } from '../../utils/dates';
import { GST_STATES, stateNameFor } from '../../utils/states';

const emptyEmi: BillEmi = { emiBankId: null, downPayment: null };
const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const money = (value?: number | null) => Number(value || 0).toFixed(2);

/** The item table's head, in the app's own blue - the same table the retail suite uses. */
const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

const cellInput = { '& .MuiInputBase-input': { py: 0.4, px: 0.75, fontSize: 12 } };

interface PropsData {
  /** Set from the Bills screen to correct an existing bill. */
  editBillId?: number;
  onDone?: () => void;
}

/**
 * The billing counter.
 *
 * <p>Two ways to find a product, side by side, because each is faster in a different
 * situation: scan or type into one box when the handset is in hand, or walk down
 * brand, model, product when the customer is asking across the counter. Either way
 * the same product ends up selected and its name fills in.
 *
 * <p>Then the batch: the same product bought twice sells at two prices, so the line
 * records which consignment it came out of.
 *
 * <p>Built for the keyboard - F2 finds a product, F4 the customer, Alt+S saves.
 */
const Billing = ({ editBillId, onDone }: PropsData = {}) => {
  const { openSnackbar } = useSnackbar();
  const [saveBill, { isLoading: saving }] = useSaveBillMutation();
  const [updateBill, { isLoading: updating }] = useUpdateBillMutation();
  const [getBillById] = useLazyGetBillByIdQuery();
  const [editingBillNo, setEditingBillNo] = useState('');

  /* customer */
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [address, setAddress] = useState('');
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [searchCustomers] = useLazySearchCustomersQuery();
  const customerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* finding a product - either path ends here */
  const [product, setProduct] = useState<Product | null>(null);
  const [productInput, setProductInput] = useState('');
  const [productOptions, setProductOptions] = useState<Product[]>([]);
  const [addingProduct, setAddingProduct] = useState(false);
  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');
  const [batchId, setBatchId] = useState('');
  // Several at once: three of the same handset is one sale, not three visits to the
  // product box. Each still becomes its own line, carrying its own serial number.
  const [imeiStockId, setImeiStockId] = useState<string[]>([]);
  const [quantity, setQuantity] = useState('1');
  const [rate, setRate] = useState('');
  const [scanValue, setScanValue] = useState('');
  const productSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productRef = useRef<HTMLInputElement>(null);
  const customerRef = useRef<HTMLInputElement>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  /* the bill */
  const [rows, setRows] = useState<BillRow[]>([]);
  const [gstApplied, setGstApplied] = useState(true);
  /**
   * The state this sale is taxed to.
   *
   * <p>Empty means the shop's own state, which is what a walk-in at the counter is.
   * Choose another and the tax becomes one IGST charge instead of a CGST/SGST pair -
   * the software does not let the counter get that pairing wrong by hand.
   */
  const [placeOfSupplyCode, setPlaceOfSupplyCode] = useState('');
  const [billDiscount, setBillDiscount] = useState('');
  const [paymentType, setPaymentType] = useState<PaymentType>('CASH');
  const [paidNow, setPaidNow] = useState('');

  /**
   * What the counter actually took, one line per way of paying.
   *
   * <p>Only used when the payment type is Split. "Half in hand, half by UPI, the rest
   * next week" is an ordinary sale, and it could not be recorded at all while a bill
   * carried a single mode - the counter had to pick one and lose the other.
   */
  const [tenders, setTenders] = useState<{ paymentMode: BillTender['paymentMode']; amount: string }[]>([
    { paymentMode: 'CASH', amount: '' },
    { paymentMode: 'UPI_CARD', amount: '' },
  ]);
  const [emi, setEmi] = useState<BillEmi>(emptyEmi);
  const [banks, setBanks] = useState<MasterRow[]>([]);

  /**
   * The bill that was just saved, waiting to be printed.
   *
   * <p>The counter should not have to leave Billing, find the bill on the Bills
   * screen and print it from there - the customer is standing in front of them. The
   * bill is kept here so the printer can be handed it straight away, and the page
   * still clears itself for the next sale the moment this is closed.
   */
  const [savedBill, setSavedBill] = useState<Bill | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const { data: shop } = useGetShopSettingQuery();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const { data: brands = [] } = useGetBrandsQuery();
  const { data: models = [] } = useGetModelsByBrandQuery(Number(brandId), { skip: !brandId });
  const { data: modelProducts = [] } = useGetProductsByModelQuery(Number(modelId), { skip: !modelId });
  const { data: batches = [], isFetching: loadingBatches } = useGetOpenBatchesQuery(Number(product?.id), {
    skip: !product?.id,
  });
  // Every handset of this product that is still in the shop, whichever consignment
  // it came in on. The batch box below filters this list rather than gating it -
  // a phone is in hand, and its batch is a fact about it, not a choice to make first.
  const { data: productImeis = [], isFetching: loadingImeis } = useGetAvailableImeisByProductQuery(Number(product?.id), {
    skip: !product?.id || product?.serialTracked === false,
  });

  const [searchProducts] = useLazySearchProductsQuery();
  const [findByImei] = useLazyFindByImeiQuery();
  const [findProductByBarcode] = useLazyFindProductByBarcodeQuery();
  const [getProductById] = useLazyGetProductByIdQuery();
  const [getMasterPage] = useGetMasterPageMutation();

  /** Serial numbers already on this bill - they cannot be sold twice. */
  const onThisBill = useMemo(() => new Set(rows.map((row) => String(row.imeiStockId ?? ''))), [rows]);

  /**
   * What the serial number box lists: every piece still in the shop, narrowed if a
   * batch is picked, less whatever is already on this bill.
   *
   * <p>Dropping the ones already added matters more than it sounds: the counter picks
   * the top of the list, and two of the same model in one sale used to mean picking
   * the second one from a list where the first was still sitting there.
   */
  const visibleImeis = useMemo(
    () =>
      productImeis
        .filter((phone) => !batchId || String(phone.batchId) === String(batchId))
        .filter((phone) => !onThisBill.has(String(phone.id))),
    [productImeis, batchId, onThisBill],
  );

  const loadBanks = useCallback(async () => {
    try {
      const response = await getMasterPage({ path: 'emi-bank', searchValue: '', page: 0, size: 100 }).unwrap();
      setBanks(response?.data || []);
    } catch {
      setBanks([]);
    }
  }, [getMasterPage]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  /* ------------------------------------------------------------- shortcuts */

  // No dependency array on purpose: the handler closes over the current bill, so
  // re-binding it each render is what keeps Alt+S from saving a stale one. The
  // listener is removed again on every cleanup, so only one is ever attached.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      /*
       * Nothing fires while anything is open on top of the bill.
       *
       * The listener runs on the way down the page, so it sees keys pressed inside a
       * dialog or a drawer as well. Alt+S typed while New Product was open saved the
       * bill underneath it, and Alt+N cleared it - both with the counter looking at a
       * different screen entirely.
       */
      if (savedBill || addingProduct || addingCustomer) return;

      if (event.key === 'F2') {
        event.preventDefault();
        productRef.current?.focus();
      } else if (event.key === 'F3') {
        event.preventDefault();
        scanRef.current?.focus();
      } else if (event.key === 'F4') {
        event.preventDefault();
        customerRef.current?.focus();
      } else if (event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleSave();
      } else if (event.altKey && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        resetBill();
      }
    };
    /*
     * Listened for on the way DOWN the page rather than on the way back up.
     *
     * The product and customer boxes are autocompletes, and while one of them has an
     * open dropdown it handles keys itself and stops some of them travelling any
     * further. A listener waiting at the window never saw those, so the shortcut did
     * nothing - but only sometimes, which is exactly how it was reported. Catching the
     * key on its way in means it reaches here first, whatever is focused.
     */
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  });

  /* --------------------------------------------------------------- editing */

  useEffect(() => {
    if (!editBillId) return;
    (async () => {
      try {
        const bill = await getBillById(editBillId).unwrap();
        setEditingBillNo(bill.billNo || '');
        setCustomerId(bill.customerId ?? null);
        setCustomerName(bill.customerName || '');
        setMobileNumber(bill.mobileNumber || '');
        setAddress(bill.address || '');
        setGstApplied(bill.gstApplied !== false);
        setPaymentType(bill.paymentType);
        setPlaceOfSupplyCode(bill.placeOfSupplyCode ?? '');
        setPaidNow(bill.paidAmount ? String(bill.paidAmount) : '');
        // A split bill has to come back as it was entered, or correcting one line of it
        // would quietly re-settle the whole bill as a single mode.
        if (bill.tenders && bill.tenders.length > 0) {
          setTenders(bill.tenders.map((tender) => ({ paymentMode: tender.paymentMode, amount: String(tender.amount) })));
        }
        setRows(
          (bill.items || []).map((item, index) => ({
            ...item,
            key: `existing-${item.id ?? index}`,
            label: item.description || item.productName || '',
          })),
        );
      } catch (error: any) {
        openSnackbar(error?.data?.message || 'Could not open the bill', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBillId]);

  /* --------------------------------------------------------------- lookups */

  const loadCustomers = useCallback(
    async (tag = '') => {
      try {
        setCustomerOptions(await searchCustomers(tag).unwrap());
      } catch {
        setCustomerOptions([]);
      }
    },
    [searchCustomers],
  );

  // Everyone the shop knows is in the box the moment it opens. It used to wait for
  // two characters, so a customer added a second ago looked as if it had not saved.
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const onCustomerTyped = (value: string) => {
    setCustomerName(value);
    setCustomerId(null);
    if (customerTimer.current) clearTimeout(customerTimer.current);
    customerTimer.current = setTimeout(() => loadCustomers(value.trim()), 400);
  };

  const loadProducts = useCallback(
    async (tag = '') => {
      try {
        setProductOptions(await searchProducts(tag).unwrap());
      } catch {
        setProductOptions([]);
      }
    },
    [searchProducts],
  );

  // What the shop sells is in the box the moment it opens; typing narrows it.
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => () => {
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    if (customerTimer.current) clearTimeout(customerTimer.current);
  }, []);

  const onProductTyped = (value: string) => {
    setProductInput(value);
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    productSearchTimer.current = setTimeout(() => loadProducts(value.trim()), 400);
  };

  /** Whichever path chose it, the product lands here and the rest follows. */
  const selectProduct = (picked: Product | null) => {
    setProduct(picked);
    setProductInput(picked?.productName ?? '');
    setBatchId('');
    setImeiStockId([]);
    setRate(picked?.sellingPrice ? String(picked.sellingPrice) : '');
    setQuantity('1');
  };

  // Counted stock has no serial number to identify it, so it sells oldest-first and
  // the first open batch is chosen for them. A handset does not: leaving the batch
  // empty means the serial number box shows every piece in the shop, which is what the
  // counter wants when it has one in hand.
  useEffect(() => {
    if (product?.serialTracked === false && !batchId && batches.length > 0) {
      const first = batches[0];
      setBatchId(String(first.id));
      if (first.sellingPrice) setRate(String(first.sellingPrice));
    }
  }, [batches, batchId, product]);

  /* ----------------------------------------------------------------- lines */

  const addRow = (...added: BillRow[]) => {
    setRows((previous) => [...previous, ...added]);
    setProduct(null);
    setProductInput('');
    loadProducts();
    setBatchId('');
    setImeiStockId([]);
    setQuantity('1');
    setRate('');
    productRef.current?.focus();
  };

  const addCurrent = () => {
    if (!product?.id) {
      openSnackbar('Pick a product first (F2)', 'warning');
      return;
    }
    const batch = batches.find((candidate) => String(candidate.id) === String(batchId));

    if (product.serialTracked !== false) {
      /*
       * Several handsets of the same model go on in one go - a customer buying three of
       * the same phone is an ordinary sale, and adding them one at a time meant picking
       * the product again for each.
       *
       * Still one row per handset, because each carries its own serial number and that
       * is what the bill has to record: months later somebody walks in with a phone and
       * the shop looks up that number to see whether it sold it and when.
       */
      if (imeiStockId.length === 0) {
        openSnackbar('Choose which piece is going out by its serial number', 'warning');
        return;
      }

      const chosen = imeiStockId
        .map((id) => productImeis.find((item: any) => String(item.id) === String(id)))
        .filter(Boolean) as any[];

      if (chosen.length !== imeiStockId.length) {
        openSnackbar('One of those handsets is no longer available', 'warning');
        return;
      }
      const alreadyOn = chosen.find((phone) => rows.some((row) => row.imeiStockId === phone.id));
      if (alreadyOn) {
        openSnackbar(`${alreadyOn.imeiNo} is already on this bill`, 'warning');
        return;
      }

      addRow(
        ...chosen.map((phone) => ({
          key: newKey(),
          productId: product.id!,
          productName: product.productName,
          serialTracked: true,
          batchId: phone.batchId ?? batch?.id ?? null,
          batchNo: phone.batchNo ?? batch?.batchNo ?? null,
          imeiStockId: phone.id,
          imeiNo: phone.imeiNo,
          label: product.productName ?? '',
          description: product.productName,
          quantity: 1,
          mrp: phone.mrp,
          rate: rate === '' ? phone.sellingPrice : Number(rate),
          discountAmount: 0,
          gstPercentage: product.gstPercentage,
        })),
      );
      return;
    }

    const qty = Number(quantity) || 0;
    if (qty <= 0) {
      openSnackbar('Quantity must be more than zero', 'warning');
      return;
    }
    if (batch && Number(batch.quantityRemaining) < qty) {
      openSnackbar(`Batch ${batch.batchNo} has only ${batch.quantityRemaining} left`, 'warning');
      return;
    }
    addRow({
      key: newKey(),
      productId: product.id,
      productName: product.productName,
      serialTracked: false,
      batchId: batch?.id ?? null,
      batchNo: batch?.batchNo ?? null,
      label: product.productName ?? '',
      description: product.productName,
      quantity: qty,
      mrp: batch?.mrp ?? product.mrp,
      rate: rate === '' ? batch?.sellingPrice ?? product.sellingPrice : Number(rate),
      discountAmount: 0,
      gstPercentage: product.gstPercentage,
    });
  };

  /**
   * Whatever came off the scanner.
   *
   * <p>A phone box carries several barcodes - IMEI 1, IMEI 2, the serial, and the
   * product's own EAN - and a counter cannot be expected to pick the right one under
   * pressure. A serial number puts that exact piece on the bill; a product barcode selects
   * the product and leaves them to choose which handset goes out, because a product
   * code cannot say which piece is leaving the shop.
   */
  const addByImei = async (raw?: string) => {
    const value = (raw ?? scanValue).replace(/[\s-]/g, '').trim();
    if (!value) return;
    try {
      const phone = await findByImei(value).unwrap();
      if (phone.status !== 'AVAILABLE') {
        openSnackbar(`Serial number ${value} is already ${phone.status.toLowerCase()}`, 'error');
        return;
      }
      if (rows.some((row) => row.imeiStockId === phone.id)) {
        openSnackbar('That handset is already on this bill', 'warning');
        return;
      }
      addRow({
        key: newKey(),
        productId: phone.productId,
        productName: phone.productName,
        serialTracked: true,
        batchId: phone.batchId ?? null,
        batchNo: phone.batchNo ?? null,
        imeiStockId: phone.id,
        imeiNo: phone.imeiNo,
        label: phone.productName,
        description: phone.productName,
        quantity: 1,
        mrp: phone.mrp,
        rate: phone.sellingPrice,
        discountAmount: 0,
        gstPercentage: phone.gstPercentage,
      });
      setScanValue('');
      scanRef.current?.focus();
    } catch {
      // Not a serial number we hold. Before giving up, try it as a product barcode - most
      // likely they scanned a different label on the same box.
      try {
        const product = await findProductByBarcode(value).unwrap();
        selectProduct(product);
        setScanValue('');
        openSnackbar(`${product.productName} - now pick which handset is going out`, 'info');
      } catch {
        openSnackbar(`Nothing found for ${value}. Scan the serial number label, or search the product by name.`, 'error');
      }
    }
  };

  const updateRow = (key: string, patch: Partial<BillRow>) => {
    setRows((previous) => previous.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  /* ---------------------------------------------------------------- totals */

  /**
   * A customer with a state of their own sets the place of supply when chosen.
   *
   * <p>A regular buyer in Karnataka should not need somebody to remember it on every
   * bill. The counter can still override the box afterwards.
   */
  const applyCustomerState = (picked: Customer | null) => {
    const code = (picked as { stateCode?: string } | null)?.stateCode;
    if (code) setPlaceOfSupplyCode(String(code));
  };

  /** The shop's own state - anything else on the bill is an inter-state sale. */
  const shopStateCode = String(shop?.stateCode ?? '');
  const interState = !!placeOfSupplyCode && !!shopStateCode && placeOfSupplyCode !== shopStateCode;

  /** What one line costs the customer: the price before tax, plus the tax on it. */
  const lineTotalOf = (row: BillRow) => {
    const lineNet = (Number(row.rate) || 0) * (Number(row.quantity) || 0) - (Number(row.discountAmount) || 0);
    const gst = gstApplied ? Number(row.gstPercentage) || 0 : 0;
    return lineNet + (lineNet * gst) / 100;
  };

  /**
   * The same arithmetic the server does, so the screen and the saved bill agree.
   *
   * <p>The rate is the price before tax and GST is added on top of it. Turning GST off
   * therefore changes the total: the tax simply stops being added.
   */
  const totals = useMemo(() => {
    let lineTotals = 0;
    let taxable = 0;
    let tax = 0;
    // What the goods came to before anything was taken off, and what was taken off the
    // lines. The summary showed neither, so money off a line changed the taxable figure
    // with nothing on screen to say why.
    let beforeDiscount = 0;
    let lineDiscount = 0;
    rows.forEach((row) => {
      const lineGross = (Number(row.rate) || 0) * (Number(row.quantity) || 0);
      const off = Number(row.discountAmount) || 0;
      const lineNet = lineGross - off;
      const gst = gstApplied ? Number(row.gstPercentage) || 0 : 0;
      const lineTax = (lineNet * gst) / 100;
      beforeDiscount += lineGross;
      lineDiscount += off;
      taxable += lineNet;
      tax += lineTax;
      lineTotals += lineNet + lineTax;
    });
    const afterDiscount = lineTotals - (Number(billDiscount) || 0);
    const rounded = Math.round(afterDiscount);
    return {
      gross: beforeDiscount,
      lineDiscount,
      taxable,
      cgst: tax / 2,
      sgst: tax / 2,
      tax,
      roundOff: rounded - afterDiscount,
      total: rounded,
    };
  }, [rows, gstApplied, billDiscount]);

  /** What the split lines add up to, and what that leaves owing. */
  const tendered = tenders.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const overTendered = paymentType === 'SPLIT' && tendered > totals.total + 0.001;

  const balance =
    paymentType === 'CREDIT'
      ? Math.max(totals.total - (Number(paidNow) || 0), 0)
      : paymentType === 'SPLIT'
        ? Math.max(totals.total - tendered, 0)
        : 0;

  const setTender = (index: number, patch: Partial<{ paymentMode: BillTender['paymentMode']; amount: string }>) =>
    setTenders((previous) => previous.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  const addTender = () =>
    setTenders((previous) => [
      ...previous,
      { paymentMode: previous.some((line) => line.paymentMode === 'CASH') ? 'UPI_CARD' : 'CASH', amount: '' },
    ]);

  const removeTender = (index: number) => setTenders((previous) => previous.filter((_line, at) => at !== index));

  /** Puts whatever is still short into this line - the arithmetic nobody should do. */
  const fillShortfall = (index: number) => {
    const others = tenders.reduce((sum, line, at) => (at === index ? sum : sum + (Number(line.amount) || 0)), 0);
    const short = Math.max(totals.total - others, 0);
    setTender(index, { amount: short > 0 ? String(Number(short.toFixed(2))) : '' });
  };

  /** Products listed under the same name - the ones easy to sell by mistake. */
  const sameNameCount = useMemo(() => {
    if (!product?.productName) return 0;
    const name = product.productName.trim().toLowerCase();
    return productOptions.filter((option) => (option.productName ?? '').trim().toLowerCase() === name).length;
  }, [productOptions, product]);

  const resetBill = () => {
    setRows([]);
    setCustomerId(null);
    setCustomerName('');
    setMobileNumber('');
    setAddress('');
    /*
     * Refilled, not emptied.
     *
     * The list of everyone the shop knows is primed once when the screen opens. Clearing
     * it here left the next bill's customer box offering nobody at all - the counter had
     * to type a name blind and the customer looked as though they had been deleted. It is
     * a lookup list, not part of the bill, so the reset reloads it rather than binning it.
     */
    loadCustomers();
    setBillDiscount('');
    setPaidNow('');
    setPaymentType('CASH');
    setTenders([
      { paymentMode: 'CASH', amount: '' },
      { paymentMode: 'UPI_CARD', amount: '' },
    ]);
    setEmi(emptyEmi);
    setGstApplied(true);
    setPlaceOfSupplyCode('');
    setProduct(null);
    setProductInput('');
    setBrandId('');
    setModelId('');
    setBatchId('');
    setImeiStockId([]);
    setQuantity('1');
    setRate('');
    setScanValue('');
  };

  const handleSave = async () => {
    if (!customerName.trim()) {
      openSnackbar('Enter the customer name (F4)', 'warning');
      return;
    }
    if (rows.length === 0) {
      openSnackbar('Add at least one item', 'warning');
      return;
    }
    if (paymentType === 'EMI' && !emi.emiBankId) {
      openSnackbar('Choose the EMI bank', 'warning');
      return;
    }
    if (paymentType === 'SPLIT') {
      if (tendered <= 0) {
        openSnackbar('Enter how much was paid, in at least one line', 'warning');
        return;
      }
      if (overTendered) {
        openSnackbar(`The payment lines add up to ${money(tendered)}, more than the bill total`, 'warning');
        return;
      }
      if (balance > 0 && !customerName.trim()) {
        openSnackbar('A bill with a balance needs a customer name (F4)', 'warning');
        return;
      }
    }

    const payload = {
      customerId,
      customerName: customerName.trim(),
      mobileNumber: mobileNumber.trim(),
      address: address.trim(),
      paymentType,
      gstApplied,
      placeOfSupplyCode: placeOfSupplyCode || undefined,
      placeOfSupply: placeOfSupplyCode ? stateNameFor(placeOfSupplyCode) : undefined,
      discountAmount: Number(billDiscount) || 0,
      paidAmount: paymentType === 'CREDIT' ? Number(paidNow) || 0 : null,
      // Only sent for a split bill; the server settles every other type from the type
      // alone, so a plain cash sale still needs nothing here.
      tenders:
        paymentType === 'SPLIT'
          ? tenders
              .filter((line) => Number(line.amount) > 0)
              .map((line) => ({ paymentMode: line.paymentMode, amount: Number(line.amount) }))
          : [],
      items: rows.map(({ key, label, serialTracked, productName, batchNo, ...item }) => item),
      emiDetails: paymentType === 'EMI' ? emi : null,
    };

    try {
      if (editBillId) {
        const saved = await updateBill({ ...payload, id: editBillId }).unwrap();
        openSnackbar(`Bill ${saved.billNo} updated`, 'success');
        onDone?.();
        return;
      }
      const saved = await saveBill(payload).unwrap();
      openSnackbar(`Bill ${saved.billNo} saved`, 'success');
      resetBill();

      /*
       * The products in the search box were fetched when the screen opened, and each
       * carries its stock figure with it. The sale just made changed that figure, so
       * without this the next bill still offers what was available before it - the
       * counter sells from a count that is already out of date.
       */
      loadProducts();

      // Ask the backend for the bill it actually wrote - bill number, round off, the
      // GST split per line - so the printed copy is the saved bill and not this
      // screen's arithmetic. If that read fails the sale is still saved; the shop is
      // told to print it from Bills.
      try {
        setSavedBill(await getBillById(Number(saved.id)).unwrap());
      } catch {
        openSnackbar(`Bill ${saved.billNo} saved. Print it from the Bills screen.`, 'info');
        productRef.current?.focus();
      }
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not save the bill', 'error');
    }
  };

  /** Printing does not close anything; only Done clears the page for the next sale. */
  const closeAfterSave = () => {
    setSavedBill(null);
    productRef.current?.focus();
  };

  const serialTracked = product?.serialTracked !== false;

  const sectionTitle = (label: string, hint?: string) => (
    <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box>
      <PageHeader title={editBillId ? `Edit bill ${editingBillNo}` : 'New Bill'}>
        <Tooltip title="F2 product · F3 scan serial number · F4 customer · Alt+S save · Alt+N new">
          <Chip icon={<KeyboardIcon />} size="small" variant="outlined" label="Shortcuts" />
        </Tooltip>
      </PageHeader>

      <Card sx={{ p: { xs: 2, md: 3 } }}>
        {/* ------------------------------------------------------- customer */}
        {sectionTitle('Customer Details')}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                options={customerOptions}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : `${option.customerName}${option.mobileNumber ? ` (${option.mobileNumber})` : ''}`
                }
                inputValue={customerName}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') onCustomerTyped(value);
                }}
                onChange={(_event, value) => {
                  /*
                   * The X clears the box by calling this with null, and the old guard
                   * only acted on a truthy value - so the name disappeared from the box
                   * while the customer, their mobile, address and place of supply all
                   * stayed on the bill. The next sale went out under the previous
                   * customer's name.
                   */
                  if (!value) {
                    setCustomerId(null);
                    setCustomerName('');
                    setMobileNumber('');
                    setAddress('');
                    // Reloaded, not emptied: clearing the box is how you start looking
                    // for somebody else, so the list has to be there to look in.
                    loadCustomers();
                    setPlaceOfSupplyCode(shopStateCode);
                    return;
                  }
                  if (typeof value !== 'string') {
                    setCustomerId(value.id ?? null);
                    setCustomerName(value.customerName);
                    setMobileNumber(value.mobileNumber || '');
                    setAddress(value.address || '');
                    applyCustomerState(value);
                  }
                }}
                renderInput={(params) => <TextField {...params} inputRef={customerRef} label="Customer * (F4)" />}
              />
              <Tooltip title="New customer">
                <IconButton
                  color="primary"
                  tabIndex={-1}
                  onClick={() => setAddingCustomer(true)}
                  aria-label="New customer"
                  sx={{ ml: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Mobile"
              size="small"
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              value={mobileNumber}
              onChange={(event) => setMobileNumber(digitsOnly(event.target.value))}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={4}>
            <TextField label="Address" size="small" fullWidth value={address} onChange={(event) => setAddress(event.target.value)} />
          </Grid>
          {/* Which state the sale is taxed to. Left on the shop's own state it is CGST +
              SGST; pick another and the whole rate becomes one IGST charge. */}
          {gstApplied && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Place of Supply"
                size="small"
                fullWidth
                value={placeOfSupplyCode}
                onChange={(event) => setPlaceOfSupplyCode(event.target.value)}
                helperText={
                  interState
                    ? `Outside ${stateNameFor(shopStateCode) || 'the shop state'} - taxed as IGST`
                    : 'Same state as the shop - taxed as CGST + SGST'
                }
              >
                <MenuItem value="">
                  {stateNameFor(shopStateCode) ? `${stateNameFor(shopStateCode)} (this shop)` : 'This shop'}
                </MenuItem>
                {GST_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.code} &nbsp;{state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={2}>
            <FormControlLabel
              control={<Switch checked={gstApplied} onChange={(event) => setGstApplied(event.target.checked)} />}
              label="GST on this bill"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* -------------------------------------------------------- product */}
        {sectionTitle('Product Selection', 'Scan a serial number, search a product, or step down brand and model')}
        <Grid container spacing={2}>
          {/* First, because it is the fast path: the handset is in hand, it gets
              scanned, and the sale is on the bill without touching anything else. */}
          <Grid item xs={12} md={3}>
            <TextField
              label="Sell by Serial Number (F3)"
              size="small"
              fullWidth
              autoFocus
              inputRef={scanRef}
              value={scanValue}
              onChange={(event) => setScanValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addByImei((event.target as HTMLInputElement).value);
                }
              }}
              helperText="A serial number adds that exact piece; a product barcode selects the product"
              InputProps={{
                endAdornment: (
                  <IconButton size="small" tabIndex={-1} onClick={() => addByImei()} aria-label="Add by serial number">
                    <QrCodeScannerIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                options={productOptions}
                value={product}
                filterOptions={(all) => all}
                inputValue={productInput}
                getOptionLabel={(option) => option.productName ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') onProductTyped(value);
                }}
                onChange={(_event, value) => selectProduct(value)}
                noOptionsText="No products found"
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{option.productName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[option.brandName, option.modelName].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={Number(option.availableStock) > 0 ? 'success' : 'error'}
                        label={`${option.availableStock ?? 0} left`}
                      />
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} inputRef={productRef} label="Search Product (F2)" placeholder="Name, barcode, brand or model" />
                )}
              />
              <Tooltip title="New product">
                <IconButton
                  color="primary"
                  tabIndex={-1}
                  onClick={() => setAddingProduct(true)}
                  aria-label="New product"
                  sx={{ ml: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              select
              label="Brand"
              size="small"
              fullWidth
              value={brandId}
              onChange={(event) => {
                setBrandId(String(event.target.value));
                setModelId('');
                selectProduct(null);
              }}
            >
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.brandName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField
              select
              label="Model"
              size="small"
              fullWidth
              disabled={!brandId}
              value={modelId}
              onChange={(event) => {
                setModelId(String(event.target.value));
                selectProduct(null);
              }}
            >
              {models.map((model) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.modelName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              select
              label="Product"
              size="small"
              fullWidth
              disabled={!modelId}
              value={product?.id ?? ''}
              onChange={async (event) => {
                const picked = modelProducts.find((item) => String(item.id) === String(event.target.value));
                if (picked?.id) {
                  // Read it fully, so specs and batches come with it.
                  try {
                    selectProduct(await getProductById(picked.id).unwrap());
                  } catch {
                    selectProduct(picked);
                  }
                }
              }}
            >
              {modelProducts.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.productName} — {item.availableStock ?? 0} left
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {product && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12}>
                <ProductProperties product={product} sameName={sameNameCount} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label={serialTracked ? 'Batch (filter)' : 'Batch'}
                  size="small"
                  fullWidth
                  value={batchId}
                  onChange={(event) => {
                    const picked = String(event.target.value);
                    setBatchId(picked);
                    setImeiStockId([]);
                    const batch = batches.find((candidate) => String(candidate.id) === picked);
                    if (batch?.sellingPrice) setRate(String(batch.sellingPrice));
                  }}
                  helperText={
                    loadingBatches
                      ? 'Reading batches...'
                      : batches.length === 0
                        ? 'No stock. Enter a purchase first.'
                        : serialTracked
                          ? 'Leave on All to see every handset'
                          : 'Oldest stock sells first'
                  }
                >
                  {/* Serial-tracked stock does not need a batch chosen first - the
                      handset decides it. */}
                  {serialTracked && <MenuItem value="">All batches ({visibleImeis.length} in stock)</MenuItem>}
                  {batches.map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      {batch.batchNo} · {shortDate(batch.receivedDate)} — {money(batch.sellingPrice)} · {batch.quantityRemaining} left
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {serialTracked ? (
                  <TextField
                    select
                    label={`Serial Number (${visibleImeis.length} available)`}
                    size="small"
                    fullWidth
                    SelectProps={{
                      multiple: true,
                      renderValue: (chosen) => {
                        const ids = chosen as string[];
                        if (ids.length === 0) return '';
                        if (ids.length === 1) {
                          return productImeis.find((item) => String(item.id) === String(ids[0]))?.imeiNo ?? '1 chosen';
                        }
                        return `${ids.length} handsets chosen`;
                      },
                    }}
                    value={imeiStockId}
                    onChange={(event) => {
                      const picked = (
                        typeof event.target.value === 'string' ? event.target.value.split(',') : (event.target.value as string[])
                      ).map(String);
                      setImeiStockId(picked);

                      /*
                       * The batch filter is deliberately left alone.
                       *
                       * Setting it to the first handset's batch narrowed this very list
                       * to that consignment, so the moment one piece was ticked every
                       * handset from any other consignment vanished - and two of the
                       * same model bought in different weeks could not be sold together
                       * at all. Each row takes its batch from its own handset when it is
                       * added, which is where that belongs.
                       *
                       * The rate is filled in only when one handset is chosen. Pick
                       * several and it is left empty on purpose: each line then prices
                       * itself from its own handset, which matters because two
                       * consignments of the same phone can sell for different amounts.
                       */
                      if (picked.length === 1) {
                        const phone = productImeis.find((item) => String(item.id) === picked[0]);
                        if (phone?.sellingPrice) setRate(String(phone.sellingPrice));
                      } else {
                        setRate('');
                      }
                    }}
                    helperText={
                      loadingImeis
                        ? 'Checking...'
                        : visibleImeis.length === 0
                          ? 'Nothing left to sell'
                          : imeiStockId.length > 1
                            ? `${imeiStockId.length} pieces - one line each`
                            : 'Pick the piece going out. Tick more than one to sell several.'
                    }
                  >
                    {visibleImeis.map((phone) => (
                      <MenuItem key={phone.id} value={String(phone.id)}>
                        <Checkbox size="small" checked={imeiStockId.includes(String(phone.id))} sx={{ py: 0, mr: 0.5 }} />
                        {phone.imeiNo} — {phone.batchNo ?? 'No batch'} · {shortDate(phone.receivedDate)} · {money(phone.sellingPrice)}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    label="Quantity"
                    type="number"
                    size="small"
                    fullWidth
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                    helperText=" "
                  />
                )}
              </Grid>
              <Grid item xs={8} sm={4} md={2}>
                <TextField label="Rate" type="number" size="small" fullWidth value={rate} onChange={(event) => setRate(event.target.value)} helperText=" " />
              </Grid>
              <Grid item xs={4} sm={2} md={1}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={addCurrent} fullWidth>
                  Add
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ---------------------------------------------------------- items */}
        <Paper sx={{ overflow: 'auto', mt: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Batch', 'Serial Number', 'Qty', 'Rate', 'Discount (₹)', 'GST %', 'Total', 'Action'].map((head) => (
                    <TableCell key={head} sx={headCell}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No items yet. Scan a serial number, search a product, or step through brand and model.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.key} hover sx={{ '& td': { py: 0.5, px: 1 } }}>
                      <TableCell sx={{ minWidth: 200, fontSize: 12, fontWeight: 500 }}>{row.label}</TableCell>
                      <TableCell sx={{ minWidth: 110, fontSize: 12 }}>
                        {row.batchNo ? <Chip size="small" variant="outlined" label={row.batchNo} /> : '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 140, fontSize: 12 }}>{row.imeiNo || '—'}</TableCell>
                      <TableCell sx={{ minWidth: 60, fontSize: 12, textAlign: 'center' }}>{row.quantity}</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          sx={cellInput}
                          value={row.rate ?? ''}
                          onChange={(event) => updateRow(row.key, { rate: event.target.value === '' ? null : Number(event.target.value) })}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 90 }}>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          sx={cellInput}
                          value={row.discountAmount ?? 0}
                          onChange={(event) => updateRow(row.key, { discountAmount: Number(event.target.value) || 0 })}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 60, fontSize: 12, textAlign: 'center' }}>
                        {gstApplied ? row.gstPercentage ?? 0 : 0}
                      </TableCell>
                      {/* Tax included, so the column adds up to the bill's net amount. */}
                      <TableCell sx={{ minWidth: 90, fontSize: 12, fontWeight: 600 }}>
                        {lineTotalOf(row).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 60 }}>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRows((previous) => previous.filter((item) => item.key !== row.key))}
                            aria-label="Remove"
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* -------------------------------------------------- payment, total */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            {sectionTitle('Payment')}
            <ToggleButtonGroup exclusive size="small" value={paymentType} onChange={(_event, value) => value && setPaymentType(value)}>
              <ToggleButton value="CASH">Cash</ToggleButton>
              <ToggleButton value="UPI_CARD">UPI / Card</ToggleButton>
              <ToggleButton value="CREDIT">Credit</ToggleButton>
              <ToggleButton value="EMI">EMI</ToggleButton>
              <ToggleButton value="SPLIT">Split</ToggleButton>
            </ToggleButtonGroup>

            {paymentType === 'SPLIT' && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  WHAT THE CUSTOMER PAID &mdash; anything short of the total is left on credit
                </Typography>

                {tenders.map((line, index) => (
                  <Stack key={index} direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <TextField
                      select
                      label="Mode"
                      size="small"
                      sx={{ width: 170 }}
                      value={line.paymentMode}
                      onChange={(event) => setTender(index, { paymentMode: event.target.value as BillTender['paymentMode'] })}
                    >
                      <MenuItem value="CASH">Cash</MenuItem>
                      <MenuItem value="UPI_CARD">UPI / Card / Bank</MenuItem>
                    </TextField>
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      sx={{ width: 150 }}
                      value={line.amount}
                      onChange={(event) => setTender(index, { amount: event.target.value })}
                    />
                    <Tooltip title="Put whatever is still short into this line">
                      <Button size="small" color="inherit" onClick={() => fillShortfall(index)}>
                        Rest
                      </Button>
                    </Tooltip>
                    {tenders.length > 1 && (
                      <Tooltip title="Remove this line">
                        <IconButton size="small" onClick={() => removeTender(index)} aria-label="Remove payment line">
                          <RemoveCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}

                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addTender} sx={{ mb: 1.5 }}>
                  Another mode
                </Button>

                {/* Said out loud, so nobody has to do it in their head at the counter. */}
                <Divider sx={{ mb: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Paying now</Typography>
                  <Typography variant="body2" fontWeight={700} color={overTendered ? 'error.main' : 'primary.main'}>
                    {money(tendered)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color={balance > 0 ? 'warning.main' : 'text.secondary'}>
                    {overTendered ? 'More than the bill total' : balance > 0 ? 'Left on credit' : 'Nothing left to pay'}
                  </Typography>
                  <Typography variant="body2" fontWeight={balance > 0 ? 700 : 400} color={overTendered ? 'error.main' : 'inherit'}>
                    {overTendered ? `Total is ${money(totals.total)}` : money(balance)}
                  </Typography>
                </Stack>
                {balance > 0 && !overTendered && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {money(balance)} will show under Customer Dues against {customerName.trim() || 'this customer'}.
                  </Typography>
                )}
              </Paper>
            )}

            {paymentType === 'CREDIT' && (
              <Stack direction="row" gap={2} alignItems="center" sx={{ mt: 2 }}>
                <TextField
                  label="Paid now"
                  type="number"
                  size="small"
                  sx={{ width: 160 }}
                  value={paidNow}
                  onChange={(event) => setPaidNow(event.target.value)}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    BALANCE
                  </Typography>
                  <Typography fontWeight={700}>{money(balance)}</Typography>
                </Box>
              </Stack>
            )}

            {paymentType === 'EMI' && (
              <EmiSection
                emi={emi}
                total={totals.total}
                banks={banks}
                onChange={(patch) => setEmi((previous) => ({ ...previous, ...patch }))}
                onBanksChanged={loadBanks}
              />
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                SUMMARY
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">Items</Typography>
                <Typography variant="body2">{rows.length}</Typography>
              </Stack>
              {/* Only worth the two extra lines when something was actually taken off a
                  line - otherwise they would say the same number three times. */}
              {totals.lineDiscount > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">{money(totals.gross)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="success.main">Item discount</Typography>
                    <Typography variant="body2" color="success.main">- {money(totals.lineDiscount)}</Typography>
                  </Stack>
                </>
              )}
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">Subtotal (taxable)</Typography>
                <Typography variant="body2">{money(totals.taxable)}</Typography>
              </Stack>
              {gstApplied &&
                (interState ? (
                  // One charge, not two: the whole rate is IGST on a sale that leaves
                  // the state.
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">IGST</Typography>
                    <Typography variant="body2">{money(totals.tax)}</Typography>
                  </Stack>
                ) : (
                  <>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">CGST</Typography>
                      <Typography variant="body2">{money(totals.cgst)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">SGST</Typography>
                      <Typography variant="body2">{money(totals.sgst)}</Typography>
                    </Stack>
                  </>
                ))}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                {/* Named in rupees on purpose. Called just "Discount" it was read as a
                    percentage, so 2 was typed meaning 2% and took off two rupees.
                    "Bill" distinguishes it from the per-line Discount column above:
                    money off a line was entered there and then looked for here. */}
                <Typography variant="body2">Bill discount (&#8377;)</Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="0.00"
                  value={billDiscount}
                  onChange={(event) => setBillDiscount(event.target.value)}
                  sx={{ width: 120, '& .MuiInputBase-input': { py: 0.4, px: 0.75, fontSize: 12, textAlign: 'right' } }}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2">Round off</Typography>
                <Typography variant="body2">{money(totals.roundOff)}</Typography>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Net Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {money(totals.total)}
                </Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 3 }}>
          {/* Opened from the bill list, so there has to be a way back to it. Kept
              separate from Clear: at a busy counter, clearing for the next sale and
              leaving the screen are different intentions. */}
          {onDone && !editBillId && (
            <Button variant="outlined" color="inherit" startIcon={<ArrowBackIcon />} onClick={() => onDone()}>
              Back to Bills
            </Button>
          )}
          <Button variant="outlined" color="inherit" onClick={editBillId ? () => onDone?.() : resetBill}>
            {editBillId ? 'Back' : 'Clear (Alt+N)'}
          </Button>
          <LoadingButton variant="contained" loading={saving || updating} onClick={handleSave}>
            {editBillId ? 'Update Bill' : 'Save Bill (Alt+S)'}
          </LoadingButton>
        </Stack>
      </Card>

      {addingCustomer && (
        <MasterDrawer
          tab="CUSTOMER"
          editData={null}
          closeDrawer={(saved) => {
            setAddingCustomer(false);
            loadCustomers();
            // Created mid-bill, so it goes on the bill rather than being searched for.
            if (saved) {
              setCustomerId(Number(saved.id) || null);
              setCustomerName(String(saved.customerName ?? ''));
              setMobileNumber(String(saved.mobileNumber ?? ''));
              setAddress(String(saved.address ?? ''));
            }
          }}
        />
      )}

      {/* Created here, sold here: the new product drops straight onto the line. */}
      <Dialog open={addingProduct} onClose={() => setAddingProduct(false)} maxWidth="lg" fullWidth>
        <ProductForm
          product={null}
          onClose={(created) => {
            setAddingProduct(false);
            if (created) selectProduct(created);
          }}
        />
      </Dialog>

      {/* Saved, and the customer is still at the counter. Print is the first button and
          takes the focus, so the usual case is one keystroke. */}
      <Dialog open={!!savedBill} onClose={closeAfterSave} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlineIcon color="success" />
          <Box>
            <Typography fontWeight={700} fontSize="1rem">
              Bill {savedBill?.billNo} saved
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {savedBill?.customerName} &middot; {savedBill?.items?.length ?? 0} item(s)
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">
              Net amount
            </Typography>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {money(savedBill?.totalAmount)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Print as many copies as you need - it can also be reprinted any time from the Bills screen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={closeAfterSave}>
            Done
          </Button>
          <Button autoFocus variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => handlePrint?.()}>
            Print Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Off-screen, and what the printer is handed. Kept out of the flow rather than
          hidden with display:none, which would give the printer an empty page. */}
      <Box sx={{ position: 'absolute', top: 0, left: '-10000px', width: '210mm' }}>
        {savedBill && <BillPrint ref={printRef} bill={savedBill} shop={shop} />}
      </Box>
    </Box>
  );
};

export default Billing;
